import { useSessionTransition } from '@/stores/sessionTransition';
import { useNavigate } from 'react-router-dom';
import { useIsFetching, useIsMutating, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/stores/auth';
import { SupersededSessionError, switchTenant } from '@/v2/utils/auth-flow';
import { landingFor } from '@/v2/utils/landing';
import { toast } from '@/v2/design-system/primitives';
import { useConfirmDiscardWork } from './useConfirmDiscardWork';

/** Both the topbar and the clinic overview use this single session transition. */
export function useTenantSwitch() {
  const switching = useSessionTransition((s) => s.isSwitchingTenant);
  const mutations = useIsMutating();
  const profileReads = useIsFetching({ queryKey: ['auth', 'me'] });
  const overviewReads = useIsFetching({ queryKey: ['consolidated-overview'] });
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const discardWork = useConfirmDiscardWork();

  const select = async (tenantId: string) => {
    if (useSessionTransition.getState().isSwitchingTenant || useAuthStore.getState().user?.impersonation || queryClient.isMutating()) return;
    if (useAuthStore.getState().user?.tenantId === tenantId) {
      navigate(landingFor(useAuthStore.getState().user?.role), { replace: true });
      return;
    }
    try {
      await switchTenant(queryClient, tenantId, (switchedUser) => {
        navigate(landingFor(switchedUser.role), { replace: true });
      });
      toast.success('Clínica alterada com sucesso!');
    } catch (error) {
      if (error instanceof SupersededSessionError) return;
      const message = error as { response?: { data?: { message?: string } }; message?: string };
      toast.error(message.response?.data?.message || message.message || 'Não foi possível trocar de clínica.');
      if (!useAuthStore.getState().isAuthenticated) navigate('/login', { replace: true });
    }
  };

  return {
    busy: switching || mutations > 0 || profileReads > 0 || overviewReads > 0,
    confirmation: discardWork.confirmation,
    selectClinic: (tenantId: string) => {
      if (tenantId === useAuthStore.getState().user?.tenantId) void select(tenantId);
      else discardWork.run(() => { void select(tenantId); });
    },
    // Overview navigation keeps the session and its drafts; only a real tenant
    // change invokes the destructive-context confirmation.
    showAll: () => navigate('/clinicas'),
  };
}
